
import { MapPin, Info, Phone, Bell } from "lucide-react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { UseFormReturn } from "react-hook-form";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { AddressMapInput, AddressData } from "@/components/ui/AddressMapInput";
import { useNavigation } from "@/hooks/useNavigation";
import { useEffect } from "react";

interface ProfileFormValues {
  displayName: string;
  phone: string;
  address: string;
  location: string;
  addressData: AddressData;
  emailBookingNotifications: boolean;
  smsBookingNotifications: boolean;
}

interface ProfileContactFormProps {
  form: UseFormReturn<ProfileFormValues>;
  loading: boolean;
}

export const ProfileContactForm = ({
  form,
  loading
}: ProfileContactFormProps) => {
  const { preferences } = useNotificationPreferences();
  const { navigateTo } = useNavigation();

  // Load notification preferences data into form when available
  useEffect(() => {
    if (preferences) {
      form.setValue('phone', preferences.notification_phone || '');
      form.setValue('emailBookingNotifications', preferences.email_booking_updates);
      form.setValue('smsBookingNotifications', preferences.sms_booking_updates);
    }
  }, [preferences, form]);

  const handleNotificationToggle = (field: 'emailBookingNotifications' | 'smsBookingNotifications', value: boolean) => {
    // Ensure at least one notification method is enabled
    if (!value) {
      const otherField = field === 'emailBookingNotifications' ? 'smsBookingNotifications' : 'emailBookingNotifications';
      const otherValue = form.getValues(otherField);
      if (!otherValue) {
        // Don't allow disabling if the other is also disabled
        return;
      }
    }
    
    form.setValue(field, value);
  };

  return (
    <div className="md:col-span-2 space-y-2 rounded-lg border p-4 bg-blue-50/50">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-blue-600" />
          <h3 className="text-base font-semibold text-blue-700">Contact Information</h3>
        </div>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigateTo('/')}
          className="text-muted-foreground hover:text-foreground"
        >
          Skip for now
        </Button>
      </div>
      
      <div className="flex items-center gap-2 mb-3 text-sm bg-blue-100 p-3 rounded-md text-blue-800">
        <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
        <p>We only share your contact information with vendors after you've booked their services.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Phone Number
              </FormLabel>
              <FormControl>
                <Input 
                  {...field}
                  placeholder="Enter your phone number"
                  disabled={loading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="addressData"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormControl>
                <AddressMapInput
                  value={field.value || { address: "", mapLink: "", directions: "" }}
                  onChange={field.onChange}
                  disabled={loading}
                  required={true}
                  showDirections={true}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Separator className="my-4" />

      {/* Booking Notifications Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-blue-600" />
          <h4 className="text-sm font-semibold text-blue-700">Booking Notifications</h4>
        </div>
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="emailBookingNotifications"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                <div className="space-y-0.5">
                  <FormLabel className="text-sm font-medium">
                    Email Notifications
                  </FormLabel>
                  <p className="text-xs text-muted-foreground">
                    Receive booking updates via email
                  </p>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => handleNotificationToggle('emailBookingNotifications', checked)}
                    disabled={loading}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="smsBookingNotifications"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                <div className="space-y-0.5">
                  <FormLabel className="text-sm font-medium">
                    SMS Notifications
                  </FormLabel>
                  <p className="text-xs text-muted-foreground">
                    Receive booking updates via SMS
                  </p>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => handleNotificationToggle('smsBookingNotifications', checked)}
                    disabled={loading}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          At least one notification method must be enabled for booking updates.
        </p>
      </div>
    </div>
  );
};
